import React, { useRef, useCallback, useMemo, useState } from 'react'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import InputAdornment from '@material-ui/core/InputAdornment'

import dateMask from '@galo-monitor/forms/masks/date.mask'
import { calendar } from '@galo-monitor/icons'
import { momentBackDateFormat, momentFriendlyDateFormat, momentFriendlyDateUSFormat } from '@galo-monitor/utils/dates'
import CalendarPicker from '@galo-monitor/web-components/CalendarPicker'
import Icon from '@galo-monitor/web-components/Icon'
import TextField from '@galo-monitor/web-components/TextField'

const InputDate = (props) => {
  const {
    value,
    onChange,
    onClick,
    adornmentStyle,
    locale,
    ...rest
  } = props

  const { detached, disabled } = rest

  const [calendarValue, setCalendarValue] = useState(value)

  const date = useMemo(
    () => detached ? value : calendarValue,
    [calendarValue, detached, value]
  )

  const inputRef = useRef()

  const calendarOptions = useMemo(
    () => ({ mode: 'single' }),
    []
  )

  const transformRender = useCallback(
    (newDate) => {
      const newMoment = moment(newDate, momentBackDateFormat, true)
      if (newMoment.isValid()) {
        return newMoment.format(
          locale === 'pt'
            ? momentFriendlyDateFormat
            : momentFriendlyDateUSFormat
        )
      }
      return newMoment._i
    },
    [locale]
  )

  const transformValue = useCallback(
    (newDate) => {
      const newMoment = moment(
        newDate,
        locale === 'pt'
          ? momentFriendlyDateFormat
          : momentFriendlyDateUSFormat,
        true
      )
      if (newMoment.isValid()) {
        return newMoment.format(momentBackDateFormat)
      }
      return newMoment._i
    },
    [locale]
  )

  // only use it outside a form
  const handleInputChange = useCallback(
    ({ target: { value: newDate } }) => {
      onChange(transformValue(newDate))
    },
    [onChange, transformValue]
  )

  // only use it inside a form
  const handleFieldChange = useCallback(
    (newDate) => {
      if (!detached) {
        if (newDate !== value && moment(newDate, momentBackDateFormat, true).isValid()) {
          setCalendarValue(newDate)
        } else {
          setCalendarValue('')
        }
      }
    },
    [detached, value]
  )

  const handleCalendarChange = useCallback(
    (newDate) => {
      if (!detached && inputRef.current.setValue) {
        inputRef.current.setValue(newDate)
      }
      onChange(newDate)
    },
    [detached, onChange]
  )

  if (detached) {
    rest.value = date
    rest.onChange = handleInputChange
  } else {
    rest.transformValue = transformValue
    rest.onFieldValueChange = handleFieldChange
  }

  const input = (
    <TextField
      data-input
      ref={ inputRef }
      { ...rest }
      autoComplete="off"
      transformRender={ transformRender }
      type="tel"
      setMask={ dateMask }
      InputProps={ {
        endAdornment: (
          <InputAdornment style={ { paddingRight: 18, ...adornmentStyle } } position="end">
            <Icon
              icon={ calendar }
              size={ 18 }
              color="#151818"
              onClick={ onClick }
              data-toggle
            />
          </InputAdornment>
        )
      } }
    />
  )

  return (
    disabled
      ? input
      : (
        <CalendarPicker
          value={ date }
          onChange={ handleCalendarChange }
          options={ calendarOptions }
        >
          {input}
        </CalendarPicker>
      )
  )
}

InputDate.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  adornmentStyle: PropTypes.object,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  locale: PropTypes.string.isRequired
}

InputDate.defaultProps = {
  value: '',
  onChange: () => {},
  adornmentStyle: {},
  onClick: () => {},
  disabled: false
}

export default InputDate
